import _ from 'lodash';
import React from 'react';

import {
  CustomOrderFormatPropertyMappingInput,
  OrderPropertyName,
  SaveCustomOrderFormatFlowInput,
  useCsvOrderFormatSettingsWidgetQuery,
  useSaveCustomOrderFormatFlowMutation
} from 'generated/graphql';

import * as types from './types';



export interface IData {
  flowId: string;
  formState: types.IFormState;
  storeCanDoCsvUploads: boolean;
}

export function useQuery(storeId: string) {
  const tuple = useCsvOrderFormatSettingsWidgetQuery({
    variables: {
      storeId
    }
  });

  return React.useMemo(
    () => {
      const propName2CurrentValue = _.keyBy(
        tuple.data?.store?.customOrderFormatFlow?.propertyMapping ?? [],
        mapping => mapping.mapToProperty.name
      );

      const data: IData = {
        flowId: tuple.data?.store?.customOrderFormatFlow?.id ?? '',
        formState: {
          prefixNewOrders: tuple.data?.store?.storeSettings.prefixNewOrders ?? false,
          mappings: _.map(tuple.data?.orderProperties, (prop): types.IMapping => ({
            isRequired: __propName2IsRequired[prop.name],
            orderPropertyName: prop.name,
            orderPropertyDisplayName: prop.displayName,
            columnLabel: propName2CurrentValue[prop.name]?.sourceDataLabel ?? '',
            defaultValueOnEmpty: propName2CurrentValue[prop.name]?.defaultValueOnEmpty ?? '',
            emptyAllowed: propName2CurrentValue[prop.name]?.defaultValueOnEmpty === ' '
          }))
        },
        storeCanDoCsvUploads: tuple.data?.store?.type.name === 'customizableFormat'
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



const __propName2IsRequired: Record<OrderPropertyName, boolean> = {
  billingAddress_company: false,
  billingAddress_countryCode: false,
  order_shippingMethod: true,
  shippingAddress_company: false,
  shippingAddress_countryCode: false,

  order_id: false,
  order_identifier: true,
  order_date: false,
  order_dateShipped: false,
  order_paymentMethod: false,
  order_shippingCarrier: false,
  order_totalDollars: false,
  order_trackingNumber: false,
  order_shippingTotalDollars: false,
  other_unboundProperty: false,

  lineItem_pricePaidDollars: false,
  order_promotionType: false,

  customer_firstName: false,
  customer_lastName: false,
  customer_fullName: false,
  customer_email: true,
  customer_phoneNumber: false,

  shippingAddress_street1: true,
  shippingAddress_street2: false,
  shippingAddress_city: true,
  shippingAddress_usStateAbbrev: true,
  shippingAddress_postalCode: true,

  billingAddress_street1: false,
  billingAddress_street2: false,
  billingAddress_city: false,
  billingAddress_usStateAbbrev: false,
  billingAddress_postalCode: false,

  lineItem_itemTotalDollars: true,
  lineItem_taxTotalDollars: false,
  lineItem_feeTotalDollars: false,
  lineItem_lineTotalDollars: false,

  lineItem_quantity: false,
  lineItem_sku: true,

  lineItem_iccid: false,
  lineItem_imei: false,
  lineItem_productName: false,

  customField_1: false,
  customField_2: false,
  customField_3: false,
  customField_4: false,
  customField_5: false,
  customField_6: false,
  customField_7: false,
  customField_8: false,
  customField_9: false,
  customField_10: false,
  customField_11: false,
  customField_12: false,
  customField_13: false,
  customField_14: false,
  customField_15: false,
  customField_16: false,
  customField_17: false,
  customField_18: false,
  customField_19: false,
  customField_20: false
};



export function useSaveCustomOrderFormatFlow(storeId: string) {
  const [fnMutate, tuple] = useSaveCustomOrderFormatFlowMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    (formState: types.IFormState, id: string) => {
      const mappingsWithColumnValues = _.filter(formState.mappings, mapping => !_.isEmpty(mapping.columnLabel));
      const propertyMapping = _.map(mappingsWithColumnValues, (mapping): CustomOrderFormatPropertyMappingInput => ({
        defaultValueOnEmpty: mapping.defaultValueOnEmpty === '' && mapping.emptyAllowed ? ' ' : mapping.defaultValueOnEmpty,
        mapToPropertyName: mapping.orderPropertyName,
        sourceDataLabel: mapping.columnLabel
      }));

      const input: SaveCustomOrderFormatFlowInput = {
        propertyMapping
      };

      if (id) {
        input.id = id;
      } else {
        input.storeId = storeId;
      }

      fnMutate({
        variables: {
          input,
          storeId,
          prefixNewOrders: formState.prefixNewOrders
        }
      });
    },
    [
      fnMutate,
      storeId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
